import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  DocumentTextIcon,
  DocumentPlusIcon,
  PencilIcon,
  EyeIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XCircleIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  BarsArrowUpIcon,
  BarsArrowDownIcon,
  UserIcon,
  PrinterIcon,
  ComputerDesktopIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon,
  ShareIcon,
  ClipboardDocumentIcon,
  WrenchScrewdriverIcon,
  CheckIcon,
  ArrowLeftIcon,
  SparklesIcon,
  CodeBracketIcon,
  PaintBrushIcon,
  ViewColumnsIcon,
  AdjustmentsHorizontalIcon,
  BackwardIcon,
  ForwardIcon
} from '@heroicons/react/24/outline';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useGetLetterTemplateQuery,
  useCreateLetterTemplateMutation,
  useUpdateLetterTemplateMutation
} from '../../services/letterApi';
import { sampleTemplates } from './sampleTemplates';
import { Card, Button, Input, Select, Modal, Textarea } from '../../components/common';
import toast from 'react-hot-toast';
import { Editor } from '@tinymce/tinymce-react';

const TemplateEditor = () => {
  const { t } = useTranslation(['common', 'letters']);
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();

  // Check if we're editing
  const isEditing = !!id;

  // Form states for template creation/editing
  const [templateForm, setTemplateForm] = useState({
    name: '',
    category: '',
    description: '',
    placeholders: [],
    htmlContent: '',
    tags: []
  });

  // UI states
  const [currentPlaceholder, setCurrentPlaceholder] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [activeView, setActiveView] = useState('preview'); // 'editor', 'preview', 'split'
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [livePreview, setLivePreview] = useState(true);
  const [editorMode, setEditorMode] = useState('visual'); // 'visual', 'html'

  // Available placeholders
  const availablePlaceholders = useMemo(() => [
    'employee_name', 'employee_code', 'designation', 'department',
    'joining_date', 'promotion_date', 'last_working_day', 'salary',
    'company_name', 'branch', 'signatory_name', 'signatory_designation',
    'date'
  ], []);

  // Category options
  const categoryOptions = [
    { value: 'offer', label: 'Offer Letter', color: 'bg-green-100 text-green-800' },
    { value: 'appointment', label: 'Appointment Letter', color: 'bg-blue-100 text-blue-800' },
    { value: 'promotion', label: 'Promotion Letter', color: 'bg-purple-100 text-purple-800' },
    { value: 'relieving', label: 'Relieving Letter', color: 'bg-red-100 text-red-800' },
    { value: 'experience', label: 'Experience Letter', color: 'bg-orange-100 text-orange-800' },
    { value: 'salary_revision', label: 'Salary Revision Letter', color: 'bg-indigo-100 text-indigo-800' }
  ];

  // API hooks
  const { data: existingTemplate, isLoading: loadingTemplate } = useGetLetterTemplateQuery(id, { skip: !id });
  const [createTemplate, { isLoading: creating }] = useCreateLetterTemplateMutation();
  const [updateTemplate, { isLoading: updating }] = useUpdateLetterTemplateMutation();

  // Get user for permissions
  const user = useSelector((state) => state.auth.user);

  // Initialize form when editing
  useEffect(() => {
    if (isEditing && existingTemplate?.data) {
      const template = existingTemplate.data;
      setTemplateForm({
        name: template.name,
        category: template.category,
        description: template.description || '',
        placeholders: [...template.placeholders],
        htmlContent: template.htmlContent,
        tags: [...(template.tags || [])]
      });
    }
  }, [isEditing, existingTemplate]);

  // Generate preview with sample data
  const generatePreview = (content = null) => {
    if (!livePreview) setIsGeneratingPreview(true);
    try {
      let html = content || templateForm.htmlContent;

      // Replace placeholders with sample data
      const sampleData = {
        'employee_name': 'John Smith',
        'employee_code': 'EMP001',
        'designation': 'Software Engineer',
        'department': 'IT Department',
        'joining_date': 'January 15, 2024',
        'promotion_date': 'October 1, 2024',
        'last_working_day': 'October 15, 2024',
        'salary': '75,000',
        'company_name': 'Tech Solutions Inc.',
        'branch': 'Head Office',
        'signatory_name': 'Jane Doe',
        'signatory_designation': 'HR Manager',
        'date': new Date().toLocaleDateString()
      };

      templateForm.placeholders.forEach(placeholder => {
        if (sampleData[placeholder]) {
          const regex = new RegExp(`{{${placeholder}}}`, 'g');
          html = html.replace(regex, sampleData[placeholder]);
        }
      });

      setPreviewHtml(html);
      if (!content) setActiveView('preview');
    } catch (error) {
      toast.error('Error generating preview');
      console.error('Preview generation error:', error);
    } finally {
      if (!livePreview) setIsGeneratingPreview(false);
    }
  };

  // Live preview effect
  useEffect(() => {
    if (livePreview && templateForm.htmlContent) {
      generatePreview(templateForm.htmlContent);
    }
  }, [templateForm.htmlContent, templateForm.placeholders, livePreview]);

  const addPlaceholder = () => {
    if (currentPlaceholder && !templateForm.placeholders.includes(currentPlaceholder)) {
      setTemplateForm(prev => ({
        ...prev,
        placeholders: [...prev.placeholders, currentPlaceholder]
      }));
      setCurrentPlaceholder('');
    }
  };

  const removePlaceholder = (placeholder) => {
    setTemplateForm(prev => ({
      ...prev,
      placeholders: prev.placeholders.filter(p => p !== placeholder)
    }));
  };

  const insertPlaceholder = (placeholder) => {
    if (editorMode === 'html') {
      // For HTML mode, insert into textarea
      const textarea = document.getElementById('htmlContent');
      if (textarea) {
        const insertText = `{{${placeholder}}}`;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newValue = templateForm.htmlContent.substring(0, start) + insertText + templateForm.htmlContent.substring(end);
        setTemplateForm(prev => ({ ...prev, htmlContent: newValue }));

        // Set cursor position after insertion
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = start + insertText.length;
          textarea.focus();
        }, 0);
      }
    } else {
      // For visual mode, insert into TinyMCE
      const editor = window.tinymce?.activeEditor;
      if (editor) {
        const insertText = `{{${placeholder}}}`;
        editor.insertContent(insertText);
        editor.focus();
      }
    }
  };

  const loadSampleTemplate = (sampleKey) => {
    const sampleTemplate = sampleTemplates[sampleKey];
    if (sampleTemplate) {
      setTemplateForm({
        name: sampleTemplate.name,
        category: sampleTemplate.category,
        description: `Sample template for ${categoryOptions.find(cat => cat.value === sampleTemplate.category)?.label}`,
        placeholders: ['employee_name', 'employee_code', 'designation', 'department', 'joining_date', 'last_working_day', 'salary', 'company_name', 'branch', 'signatory_name', 'signatory_designation', 'date'],
        htmlContent: sampleTemplate.htmlContent,
        tags: []
      });
      toast.success(`Loaded sample template: ${sampleTemplate.name}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Clean up placeholders that are not actually used in the HTML content
      let cleanedPlaceholders = templateForm.placeholders.filter(placeholder => {
        const regex = new RegExp(`\\{\\{\\s*${placeholder}\\s*\\}\\}`, 'g');
        return regex.test(templateForm.htmlContent);
      });

      // Prepare cleaned template form
      const cleanedTemplateForm = {
        ...templateForm,
        placeholders: cleanedPlaceholders
      };

      if (isEditing) {
        await updateTemplate({ id, templateData: cleanedTemplateForm }).unwrap();
        toast.success('Template updated successfully');
        navigate('/letters');
      } else {
        await createTemplate(cleanedTemplateForm).unwrap();
        toast.success('Template created successfully');
        navigate('/letters');
      }
    } catch (error) {
      toast.error(error.data?.message || `Failed to ${isEditing ? 'update' : 'create'} template`);
    }
  };

  if (loadingTemplate) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="animate-pulse flex space-x-4">
          <div className="h-12 w-12 bg-blue-200 rounded-full"></div>
          <div className="h-12 flex-1 bg-white rounded-lg p-4"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="bg-white/90 backdrop-blur-sm border-b border-white/20 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="py-4">
            {/* Mobile header - Back button and title */}
            <div className="flex items-center justify-between mb-3 lg:hidden">
              <button
                onClick={() => navigate('/letters')}
                className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-900 bg-white/50 rounded-lg hover:bg-white/80 transition-all duration-200 group"
              >
                <ArrowLeftIcon className="h-5 w-5 mr-2 group-hover:-translate-x-1 transition-transform duration-200" />
                <span className="text-sm font-medium">Back</span>
              </button>
              <h1 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent text-center flex-1">
                {isEditing ? 'Edit Template' : 'Create Template'}
              </h1>
              <div className="w-20"></div> {/* Spacer */}
            </div>

            {/* Desktop header */}
            <div className="hidden lg:flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/letters')}
                  className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-900 bg-white/50 rounded-lg hover:bg-white/80 transition-all duration-200 group"
                >
                  <ArrowLeftIcon className="h-5 w-5 mr-2 group-hover:-translate-x-1 transition-transform duration-200" />
                  Back to Letters
                </button>
                <div>
                  <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                    {isEditing ? 'Edit Letter Template' : 'Create Letter Template'}
                  </h1>
                  <p className="text-sm text-gray-600">
                    {isEditing ? 'Modify the existing template' : 'Build professional letter templates with HTML previews'}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Button
                  onClick={() => activeView === 'preview' ? setActiveView('editor') : generatePreview()}
                  variant="outline"
                  size="sm"
                  loading={isGeneratingPreview}
                  className={`${activeView === 'preview' ? '' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
                >
                  {activeView === 'preview' ? (
                    <>
                      <CodeBracketIcon className="h-4 w-4 mr-2" />
                      <span className="hidden sm:inline">Back to Editor</span>
                      <span className="sm:hidden">Editor</span>
                    </>
                  ) : (
                    <>
                      <EyeIcon className="h-4 w-4 mr-2" />
                      <span className="hidden sm:inline">Preview</span>
                      <span className="sm:hidden">Preview</span>
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleSubmit}
                  loading={creating || updating}
                  className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
                >
                  <span className="hidden sm:inline">{isEditing ? 'Update Template' : 'Create Template'}</span>
                  <span className="sm:hidden">{isEditing ? 'Update' : 'Create'}</span>
                </Button>
                <div className="flex items-center">
                  <button
                    onClick={() => setLivePreview(!livePreview)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                      livePreview ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                        livePreview ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className="hidden md:inline ml-3 text-sm font-medium text-gray-700">
                    Live Preview
                  </span>
                </div>
              </div>
            </div>

            {/* Mobile subtitle */}
            <p className="text-xs text-gray-500 text-center lg:hidden">
              {isEditing ? 'Modify template' : 'Professional letter templates'}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 lg:py-6">
          {/* Sample Templates Banner - Only for new templates */}
          {!isEditing && (
            <div className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 border border-blue-200 rounded-xl p-4 sm:p-6 mb-4 lg:mb-6 shadow-sm">
              <div className="flex items-center mb-3 sm:mb-4">
                <SparklesIcon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 mr-2 sm:mr-3" />
                <h3 className="text-sm sm:text-lg font-semibold text-blue-900">Quick Start with Sample Templates</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2 sm:gap-3">
                {Object.entries(sampleTemplates).map(([key, template]) => (
                  <button
                    key={key}
                    onClick={() => loadSampleTemplate(key)}
                    className="p-3 sm:p-4 bg-white/80 hover:bg-white border border-blue-200 rounded-lg text-left transition-all duration-200 hover:shadow-md hover:border-blue-300 group"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${categoryOptions.find(cat => cat.value === template.category)?.color}`}>
                        {categoryOptions.find(cat => cat.value === template.category)?.label}
                      </div>
                      <PlusIcon className="h-4 w-4 text-blue-500 group-hover:scale-110 transition-transform" />
                    </div>
                    <div className="font-medium text-blue-900 text-sm">{template.name}</div>
                    <div className="text-xs text-blue-700 mt-1">Professional template with sample placeholders</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Content Areas */}
          <div className={`${
            activeView === 'split'
              ? 'grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8'
              : activeView === 'editor'
                ? 'grid grid-cols-1'
                : 'grid grid-cols-1'
          }`}>

            {/* Editor Panel */}
            <div className={`${
              activeView === 'preview'
                ? 'hidden lg:block lg:col-span-1'
                : activeView === 'editor'
                  ? 'block'
                  : 'block lg:col-span-1'
            } space-y-3 lg:space-y-4`}>

              {/* Compact Configuration Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-4">
                {/* Template Configuration Section - Compact */}
                <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden h-fit">
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3">
                    <h2 className="text-lg font-bold text-white flex items-center">
                      <DocumentTextIcon className="h-5 w-5 mr-2" />
                      Template Configuration
                    </h2>
                    <p className="text-blue-100 text-xs mt-1">Basic settings</p>
                  </div>
                  <div className="p-4 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label className="block text-xs font-semibold text-gray-800">
                          Template Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                          type="text"
                          value={templateForm.name}
                          onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                          placeholder="e.g., Offer Letter Template"
                          className="h-9 text-sm border-gray-300 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="block text-xs font-semibold text-gray-800">
                          Category <span className="text-red-500">*</span>
                        </label>
                        <Select
                          value={templateForm.category}
                          onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value })}
                          className="h-9 text-sm border-gray-300 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          required
                        >
                          <option value="">Select Category</option>
                          {categoryOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <label className="block text-xs font-semibold text-gray-800">
                        Description <span className="text-gray-500 font-normal">(Optional)</span>
                      </label>
                      <Textarea
                        value={templateForm.description}
                        onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                        placeholder="Describe this template..."
                        rows={2}
                        className="text-sm border-gray-300 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Placeholders Management Section - Compact */}
                <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden h-fit">
                  <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-3">
                    <h3 className="text-lg font-bold text-white flex items-center">
                      <WrenchScrewdriverIcon className="h-5 w-5 mr-2" />
                      Placeholder Management
                    </h3>
                    <p className="text-indigo-100 text-xs mt-1">Dynamic data fields</p>
                  </div>
                  <div className="p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Available Placeholders</h4>
                        <p className="text-xs text-gray-600">Click to add</p>
                      </div>
                      <div className="bg-blue-50 px-2 py-1 rounded-full">
                        <span className="text-xs font-medium text-blue-700">
                          {templateForm.placeholders.length} Active
                        </span>
                      </div>
                    </div>

                    {/* Scrollable placeholder grid */}
                    <div className="max-h-48 overflow-y-auto border border-gray-100 rounded-lg">
                      <div className="grid grid-cols-2 gap-2 p-2">
                        {availablePlaceholders.map(placeholder => (
                          <button
                            key={placeholder}
                            type="button"
                            onClick={() => setCurrentPlaceholder(placeholder)}
                            className="p-2 bg-gray-50 hover:bg-blue-50 border border-gray-200 hover:border-blue-300 rounded text-left transition-all duration-200 hover:shadow-sm group"
                          >
                            <div className="text-xs font-mono text-blue-600 group-hover:text-blue-700 break-all">
                              <code className="text-xs">{`{{${placeholder}}}`}</code>
                            </div>
                            <div className="text-xs text-gray-600 group-hover:text-blue-600 mt-1 capitalize leading-tight">
                              {placeholder.replace('_', ' ')}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="border-t pt-3">
                      <div className="flex space-x-2">
                        <Input
                          type="text"
                          value={currentPlaceholder}
                          onChange={(e) => setCurrentPlaceholder(e.target.value)}
                          placeholder="Custom placeholder"
                          className="flex-1 h-8 text-sm border-gray-300 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                        <Button
                          type="button"
                          onClick={addPlaceholder}
                          variant="outline"
                          className="h-8 px-4 text-xs border-indigo-300 text-indigo-600 hover:bg-indigo-50"
                        >
                          <PlusIcon className="h-3 w-3 mr-1" />
                          Add
                        </Button>
                      </div>
                    </div>

                    {templateForm.placeholders.length > 0 && (
                      <div className="border-t pt-3">
                        <h5 className="text-xs font-semibold text-gray-900 mb-2">
                          Active ({templateForm.placeholders.length})
                        </h5>
                        <div className="max-h-24 overflow-y-auto">
                          <div className="flex flex-wrap gap-1">
                            {templateForm.placeholders.map(placeholder => (
                              <div
                                key={placeholder}
                                className="inline-flex items-center px-2 py-1 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-full group hover:shadow-sm transition-shadow"
                              >
                                <code className="text-xs text-indigo-700 font-mono">
                                  {`{{${placeholder}}}`}
                                </code>
                                <button
                                  onClick={() => removePlaceholder(placeholder)}
                                  className="ml-1 p-0.5 rounded-full hover:bg-red-100 text-indigo-400 hover:text-red-600 transition-colors"
                                >
                                  <XCircleIcon className="h-2 w-2" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* HTML Editor Section */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4">
                  <h3 className="text-xl font-bold text-white flex items-center">
                    <CodeBracketIcon className="h-6 w-6 mr-3" />
                    HTML Template Content
                  </h3>
                  <p className="text-green-100 text-sm mt-1">Design your letter template with HTML</p>
                </div>
                <div className="p-6 space-y-6">
                  {/* Editor Mode Toggle */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      <span className="inline-flex items-center px-3 py-1 bg-red-50 text-red-700 text-xs font-medium rounded-full">
                        Required *
                      </span>
                      <div className="flex bg-gray-100 rounded-lg p-1">
                        <button
                          onClick={() => setEditorMode('visual')}
                          className={`px-3 py-1 text-xs font-medium rounded transition-all duration-200 ${
                            editorMode === 'visual'
                              ? 'bg-white text-green-700 shadow-sm'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          📝 Visual Editor
                        </button>
                        <button
                          onClick={() => setEditorMode('html')}
                          className={`px-3 py-1 text-xs font-medium rounded transition-all duration-200 ${
                            editorMode === 'html'
                              ? 'bg-white text-green-700 shadow-sm'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          💻 HTML Source
                        </button>
                      </div>
                    </div>
                    {templateForm.placeholders.length > 0 && (
                      <div className="flex flex-wrap gap-1 max-w-md">
                        <div className="text-xs text-gray-600 mr-2 hidden sm:block">Quick insert:</div>
                        {templateForm.placeholders.slice(0, 3).map(placeholder => (
                          <button
                            key={placeholder}
                            type="button"
                            onClick={() => insertPlaceholder(placeholder)}
                            className="px-2 py-1 text-xs bg-green-100 hover:bg-green-200 text-green-800 rounded transition-colors font-mono"
                            title={`Insert {{${placeholder}}}`}
                          >
                            {placeholder}
                          </button>
                        ))}
                        {templateForm.placeholders.length > 3 && (
                          <span className="text-xs text-gray-500 px-2">+{templateForm.placeholders.length - 3} more</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* TinyMCE Editor */}
                  <div className="border-2 border-gray-200 rounded-xl overflow-hidden bg-white">
                    {editorMode === 'visual' ? (
                      <Editor
                        apiKey="y0cwu5ajieg5833cy3zsnqwhj9300k3gpuz3c1yeknuxqpyl" // You'll need to get a free API key from TinyMCE
                        value={templateForm.htmlContent}
                        onEditorChange={(content) => setTemplateForm(prev => ({ ...prev, htmlContent: content }))}
                        init={{
                          height: 500,
                          menubar: false,
                          plugins: [
                            'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                            'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                            'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount', 'paste'
                          ],
                          toolbar: 'undo redo | blocks | ' +
                            'bold italic forecolor | alignleft aligncenter ' +
                            'alignright alignjustify | bullist numlist outdent indent | ' +
                            'removeformat | code fullscreen preview',
                          content_style: `
                            body {
                              font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
                              font-size: 14px;
                              line-height: 1.6;
                              color: #374151;
                            }
                            p { margin: 0 0 1rem 0; }
                            h1, h2, h3, h4, h5, h6 {
                              color: #1f2937;
                              font-weight: 600;
                              margin: 1.5rem 0 0.5rem 0;
                            }
                          `,
                          branding: false,
                          promotion: false,
                          contextmenu: 'link image table configurepermanentpen',
                          paste_data_images: false,
                          automatic_uploads: false,
                          images_upload_handler: function (blobInfo, success, failure) {
                            // Handle image uploads if needed
                            failure('Image uploads not supported in templates');
                          }
                        }}
                      />
                    ) : (
                      <Textarea
                        id="htmlContent"
                        value={templateForm.htmlContent}
                        onChange={(e) => setTemplateForm({ ...templateForm, htmlContent: e.target.value })}
                        placeholder={`<div style="font-family: Arial, sans-serif;">
  <div style="text-align: center; margin-bottom: 30px;">
    <h1 style="color: #2563eb;">{{company_name}}</h1>
    <h2>{{employee_name}} - Offer Letter</h2>
  </div>
  <p>Dear {{employee_name}},</p>
  <p>We are pleased to offer you the position of {{designation}}...</p>
</div>`}
                        rows={25}
                        required
                        className="w-full font-mono text-sm border-0 focus:ring-0 resize-none leading-6 p-6"
                        style={{ minHeight: '500px' }}
                      />
                    )}
                  </div>

                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
                    <div className="flex items-start">
                      <div className="p-1 bg-blue-100 rounded-full mr-3 mt-0.5">
                        <svg className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h5 className="text-sm font-semibold text-blue-900 mb-2">HTML Template Tips</h5>
                        <ul className="text-xs text-blue-800 space-y-1">
                          <li>• Use <code className="bg-blue-100 px-1 py-0.5 rounded text-xs">{'{{placeholder}}'}</code> syntax for dynamic data</li>
                          <li>• Click placeholder buttons above to insert them quickly</li>
                          <li>• Support for CSS styling and full HTML structure</li>
                          <li>• Preview updates automatically on the right panel</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Preview Panel */}
            <div className={`${activeView === 'editor' ? 'hidden lg:block' : ''}`}>
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-4">
                  <h3 className="text-xl font-bold text-white flex items-center">
                    <EyeIcon className="h-6 w-6 mr-3" />
                    Live Preview
                  </h3>
                  <p className="text-purple-100 text-sm mt-1">See your template come to life</p>
                </div>
                <div className="p-6">
                  <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl overflow-hidden min-h-96 relative">
                    {previewHtml ? (
                      <div
                        className="p-8 bg-white w-full h-full"
                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                        style={{
                          maxWidth: 'none',
                          boxShadow: 'inset 0 0 20px rgba(0,0,0,0.02)'
                        }}
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center h-96 text-center text-gray-500">
                        <div className="bg-purple-100 p-4 rounded-full mb-4">
                          <EyeIcon className="h-12 w-12 text-purple-400" />
                        </div>
                        <h3 className="text-lg font-semibold mb-2 text-gray-700">No Preview Generated</h3>
                        <p className="text-sm text-gray-600 mb-6 max-w-sm">
                          {livePreview
                            ? 'Start typing in the HTML editor to see your template come to life'
                            : 'Enable live preview or click below to generate a preview'
                          }
                        </p>
                        {!livePreview && (
                          <Button
                            onClick={generatePreview}
                            loading={isGeneratingPreview}
                            className="bg-purple-600 hover:bg-purple-700 text-white"
                          >
                            Generate Preview
                          </Button>
                        )}
                      </div>
                    )}

                    {/* Preview watermark */}
                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs text-gray-500 border">
                      Preview Mode
                    </div>
                  </div>

                  {previewHtml && (
                    <div className="mt-6 bg-gradient-to-r from-gray-50 to-purple-50 border border-purple-200 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-gray-900 flex items-center">
                          <svg className="h-4 w-4 mr-2 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Sample Data Applied
                        </h4>
                        <div className="bg-purple-100 px-2 py-1 rounded-full">
                          <span className="text-xs font-medium text-purple-700">
                            {templateForm.placeholders.length} placeholders filled
                          </span>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                        <div className="flex justify-between">
                          <span className="font-medium text-gray-600">Employee:</span>
                          <span className="text-gray-900">John Smith (EMP001)</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium text-gray-600">Position:</span>
                          <span className="text-gray-900">Software Engineer</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium text-gray-600">Company:</span>
                          <span className="text-gray-900">Tech Solutions Inc.</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium text-gray-600">Date:</span>
                          <span className="text-gray-900">{new Date().toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TemplateEditor;
